import React, { createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { MobileNav } from './MobileNav';
import { MoreSheet } from './MoreSheet';
import { CreateSheet } from './CreateSheet';
import { M } from './theme';

/** No global top header — each screen renders its own. `focusMode` hides the dock for edge-to-edge flows. */

interface MobileDockOverlayContextValue {
  setOverlayOpen: (open: boolean) => void;
  openCreate: () => void;
}

const MobileDockOverlayContext = createContext<MobileDockOverlayContextValue | null>(null);

/** Lets a full-screen overlay hide the floating nav; `FullScreen` calls this with its own `open` prop. */
export function useMobileDockOverlay(open: boolean): void {
  const ctx = useContext(MobileDockOverlayContext);
  useEffect(() => {
    if (!ctx || !open) return undefined;
    ctx.setOverlayOpen(true);
    return () => ctx.setOverlayOpen(false);
  }, [open, ctx]);
}

/** Opens the shared mobile Create sheet from a dedicated mobile screen. */
export function useMobileCreateSheet(): () => void {
  const ctx = useContext(MobileDockOverlayContext);
  return ctx?.openCreate ?? (() => undefined);
}

export function MobileShell({ focusMode }: { focusMode: boolean }) {
  const location = useLocation();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Close the sheets whenever the route changes (a tile/action navigated away).
  useEffect(() => {
    setMoreOpen(false);
    setCreateOpen(false);
  }, [location.pathname]);

  const dockVisible = !focusMode && !overlayOpen;

  return (
    <MobileDockOverlayContext.Provider value={{ setOverlayOpen, openCreate: () => setCreateOpen(true) }}>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden" style={{ background: M.bg }}>
        {/*
         * Dedicated mobile screens pin themselves with `absolute inset-0` and
         * own their scroll, so they contribute no in-flow height here. Routes
         * WITHOUT a dedicated mobile screen (e.g. collection/test detail pages)
         * render as normal in-flow content — `overflow-y-auto` lets those scroll
         * instead of being clipped by the previous `overflow-hidden`.
         */}
        <main className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto scrollbar-thin">
          {/*
           * Dedicated mobile screens wrap in <MobileScreen> (absolute inset-0),
           * so they pin to this <main> and ignore the padded wrapper below.
           * Unported routes render their desktop page in-flow here: this wrapper
           * gives them the side padding they'd get from the desktop shell plus
           * bottom clearance for the floating dock. `overflow-x-hidden` above is
           * the safety net that stops any too-wide desktop page from scrolling
           * the shell sideways. Focus workspaces stay edge-to-edge (no wrapper).
           */}
          {focusMode ? (
            <Outlet key={location.pathname} />
          ) : (
            <div className="px-4 pb-[calc(92px+env(safe-area-inset-bottom))] pt-3">
              <Outlet key={location.pathname} />
            </div>
          )}
        </main>
        {dockVisible && (
          <MobileNav onMore={() => setMoreOpen(true)} onCreate={() => setCreateOpen(true)} />
        )}
        <MoreSheet open={dockVisible && moreOpen} onClose={() => setMoreOpen(false)} />
        <CreateSheet open={dockVisible && createOpen} onClose={() => setCreateOpen(false)} />
      </div>
    </MobileDockOverlayContext.Provider>
  );
}

export default MobileShell;
